import { a as __exportAll, i as css, r as cva, t as CardFrame } from "./card-frame-BMI32BoD.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Image } from "@tecnobit-srl/content-types-image/lazy";
import { jsx, jsxs } from "react/jsx-runtime";
import { FontAwesome } from "@tecnobit-srl/ui-font-awesome";
import { YouTubeVideo } from "@tecnobit-srl/content-types-you-tube-video/lazy";
import { Content } from "@tecnobit-srl/content-types-store";
import { isValidContentArray } from "@tecnobit-srl/hook-use-valid-content";
import { Markdown } from "@tecnobit-srl/content-types-markdown/lazy";
import { ThemeProvider, useChildTheme } from "@tecnobit-srl/ui-theme";
//#region src/card-mini-media.tsx
function CardMiniMedia({ selected, onClick, sliderColor, color, children }) {
	const _onClick = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		onClick?.();
	}, [onClick]);
	return /* @__PURE__ */ jsx("div", {
		role: "presentation",
		className: "group",
		onClick: _onClick,
		onMouseEnter: _onClick,
		children: /* @__PURE__ */ jsx("div", {
			className: cva({
				base: {
					position: "relative",
					w: "45px",
					h: "45px",
					cursor: "pointer",
					borderWidth: "2px",
					borderStyle: "solid",
					borderRadius: "4px",
					overflow: "hidden",
					"&:hover": { opacity: 1 }
				},
				variants: {
					selected: {
						true: { opacity: 1 },
						false: { opacity: .45 }
					},
					sliderColor: {
						light: { borderColor: "black" },
						dark: { borderColor: "white" },
						default: {}
					},
					color: {
						primary: {},
						secondary: {},
						white: {}
					}
				},
				compoundVariants: [
					{
						sliderColor: "default",
						color: "primary",
						css: { borderColor: "accent.400" }
					},
					{
						sliderColor: "default",
						color: "secondary",
						css: { borderColor: "secondary.400" }
					},
					{
						sliderColor: "default",
						color: "white",
						css: { borderColor: "grey.400" }
					}
				]
			})({
				selected,
				sliderColor,
				color
			}),
			children
		})
	});
}
//#endregion
//#region src/card-mini-image.tsx
function CardMiniImage({ selected, onClick, sliderColor, color, imageProps }) {
	return /* @__PURE__ */ jsx(CardMiniMedia, {
		selected,
		onClick,
		sliderColor,
		color,
		children: imageProps.src ? /* @__PURE__ */ jsx("img", {
			src: imageProps.src,
			className: css({
				objectFit: "cover",
				minH: "full"
			}),
			alt: ""
		}) : /* @__PURE__ */ jsx(Image, {
			...imageProps,
			useGallery: false,
			fullWidth: true,
			containerCss: css.raw({
				rounded: "0 !important",
				mb: "0 !important",
				...imageProps.containerCss
			}),
			css: css.raw({
				h: "full",
				...imageProps.css
			})
		})
	});
}
//#endregion
//#region src/card-image.tsx
function CardImage({ src, containerCss = {}, css: cssFromProp = {}, imageSize = "full", mainImageAlt, ...props }) {
	if (src) return /* @__PURE__ */ jsx("img", {
		src,
		alt: mainImageAlt,
		className: css({
			borderRadius: "md",
			w: "full",
			objectFit: "cover"
		}, cssFromProp)
	});
	return /* @__PURE__ */ jsx(Image, {
		...props,
		useGallery: false,
		fullWidth: imageSize === "full",
		containerCss: css.raw({
			w: imageSize === "full" ? "full" : "auto",
			h: imageSize === "full" ? "full" : "auto",
			mb: "0 !important",
			...containerCss
		}),
		css: css.raw({
			h: "full",
			...cssFromProp
		})
	});
}
//#endregion
//#region src/card-mini-video.tsx
function CardMiniVideo({ selected, onClick, sliderColor, color, youtubeId = "JksHn32ASpU" }) {
	return /* @__PURE__ */ jsxs(CardMiniMedia, {
		selected,
		onClick,
		sliderColor,
		color,
		children: [/* @__PURE__ */ jsx("img", {
			src: `https://img.youtube.com/vi/${youtubeId}/0.jpg`,
			alt: youtubeId,
			className: css({
				minW: "100%",
				minH: "100%",
				objectFit: "cover",
				transition: "transform 0.15s ease"
			})
		}), /* @__PURE__ */ jsx("div", {
			className: css({
				position: "absolute",
				top: 0,
				left: 0,
				w: "full",
				h: "full",
				margin: "auto",
				display: "flex",
				flexDir: "column",
				alignItems: "center",
				justifyContent: "center",
				bgColor: "black/50"
			}),
			children: /* @__PURE__ */ jsx(FontAwesome, {
				css: css.raw({ color: "white" }),
				size: 1,
				name: "fa-solid fa-play"
			})
		})]
	});
}
//#endregion
//#region src/card-video.tsx
function CardVideo(props) {
	return /* @__PURE__ */ jsx(YouTubeVideo, { ...props });
}
//#endregion
//#region src/card-slider.tsx
function CardMainMedia({ mediaProps, imageSize, mainImageAlt, css: cssFromProps }) {
	return useMemo(() => {
		if (!mediaProps) return null;
		if (mediaProps.name === "cardItemImage") return /* @__PURE__ */ jsx(CardImage, {
			imageSize,
			mainImageAlt,
			css: cssFromProps,
			...mediaProps
		});
		return /* @__PURE__ */ jsx(CardVideo, { ...mediaProps });
	}, [
		mediaProps,
		imageSize,
		mainImageAlt,
		cssFromProps
	]);
}
function CardSlider({ images = [], imageSize = "full", imageAspectRatio = "16by9", sliderColor = "default", color = "primary", children, mainImageAlt, css: cssFromProps, imageCss }) {
	const [currentIndex, setCurrentIndex] = useState(0);
	const Selector = useCallback(() => {
		if (images.length < 2) return null;
		return /* @__PURE__ */ jsxs("div", {
			className: cva({
				base: {
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					justifyContent: "flex-start",
					gap: "8px",
					padding: "8px",
					overflowX: "auto",
					position: "absolute",
					left: 0,
					right: 0,
					bottom: 0,
					transition: "transform .2s ease",
					transform: "translateY(100%)",
					_groupHover: { transform: "translateY(0)" }
				},
				variants: {
					sliderColor: {
						light: { backgroundColor: "white/45" },
						dark: { backgroundColor: "black/45" },
						default: {}
					},
					color: {
						primary: {},
						secondary: {},
						white: {}
					}
				},
				compoundVariants: [
					{
						sliderColor: "default",
						color: "primary",
						css: { backgroundColor: "accent.100/45" }
					},
					{
						sliderColor: "default",
						color: "secondary",
						css: { backgroundColor: "secondary.100/45" }
					},
					{
						sliderColor: "default",
						color: "white",
						css: { backgroundColor: "white/45" }
					}
				]
			})({
				sliderColor,
				color
			}),
			children: [/* @__PURE__ */ jsx("div", { className: css({
				position: "absolute",
				top: 0,
				left: 0,
				w: "full",
				h: "full",
				backdropFilter: "blur(0px)",
				transition: "backdrop-filter .2s ease",
				_groupHover: { backdropFilter: "blur(2px)" }
			}) }), images.map((props, i) => props.name === "cardItemImage" ? /* @__PURE__ */ jsx(CardMiniImage, {
				imageProps: props,
				selected: i === currentIndex,
				onClick: () => {
					setCurrentIndex(i);
				},
				sliderColor,
				color
			}, `${props.name}-${i.toString()}`) : /* @__PURE__ */ jsx(CardMiniVideo, {
				youtubeId: props.youtubeId,
				selected: i === currentIndex,
				onClick: () => {
					setCurrentIndex(i);
				},
				sliderColor,
				color
			}, `${props.name}-${i.toString()}`))]
		});
	}, [
		images,
		sliderColor,
		color,
		currentIndex
	]);
	const style = useMemo(() => {
		return { ...imageAspectRatio !== "original" ? { aspectRatio: imageAspectRatio.replace("by", "/") } : {} };
	}, [imageAspectRatio]);
	useEffect(() => {
		if (!images.length) setCurrentIndex(-1);
	}, [images.length]);
	if (currentIndex === -1) return null;
	return /* @__PURE__ */ jsxs("div", {
		className: css({
			position: "relative",
			roundedBottom: "md",
			overflow: "hidden",
			display: "flex",
			wordBreak: "break-all"
		}, cssFromProps),
		style,
		children: [
			/* @__PURE__ */ jsx(CardMainMedia, {
				imageSize,
				mediaProps: images[currentIndex],
				mainImageAlt,
				css: imageCss
			}),
			/* @__PURE__ */ jsx(Selector, {}),
			children
		]
	});
}
//#endregion
//#region src/card-buttons.tsx
function CardButtons({ buttons = [] }) {
	if (!buttons.length) return null;
	if (isValidContentArray(buttons)) return /* @__PURE__ */ jsx("div", {
		className: css({
			flex: 1,
			alignSelf: buttons[0].width === "full" ? void 0 : "flex-start"
		}),
		children: /* @__PURE__ */ jsx(Content, { ...buttons[0] })
	});
	return /* @__PURE__ */ jsx("div", {
		className: css({
			display: "flex",
			flexDirection: "column",
			gap: "2"
		}),
		children: buttons
	});
}
//#endregion
//#region src/card-markdown.tsx
function CardMarkdown({ markdown = "", color, variant, highlightText = [], children }) {
	const theme = useChildTheme(color, variant);
	if (children) return /* @__PURE__ */ jsx(ThemeProvider, {
		theme,
		columnAlign: "start",
		children
	});
	if (!markdown) return null;
	return /* @__PURE__ */ jsx(ThemeProvider, {
		theme,
		columnAlign: "start",
		children: /* @__PURE__ */ jsx(Markdown, {
			highlightText,
			highlightColor: color,
			highlightVariant: variant,
			children: markdown
		})
	});
}
//#endregion
//#region src/card.tsx
var card_exports = /* @__PURE__ */ __exportAll({ Card: () => Card });
function Card({ images, imageSize, imageAspectRatio, sliderColor, buttons, markdown, variant = "solid", color = "primary", elevated = false, href, target, onClick, children, highlightText, sliderChildren, verticalStretch = false, mainImageAlt, css: cssFromProps, imagesCss, imageCss, ignorePadding }) {
	return /* @__PURE__ */ jsx(CardFrame, {
		variant,
		color,
		elevated,
		href,
		target,
		onClick,
		verticalStretch,
		css: cssFromProps,
		ignorePadding,
		children: /* @__PURE__ */ jsxs("div", {
			className: css({
				h: "full",
				display: "flex",
				flexDirection: "column",
				justifyContent: "space-between",
				gap: "4"
			}),
			children: [/* @__PURE__ */ jsxs("div", {
				className: css({
					h: "full",
					display: "flex",
					flexDirection: "column",
					gap: "4"
				}),
				children: [/* @__PURE__ */ jsx(CardSlider, {
					imageSize,
					images,
					imageAspectRatio,
					sliderColor,
					color,
					mainImageAlt,
					css: imagesCss,
					imageCss,
					children: sliderChildren
				}), /* @__PURE__ */ jsx(CardMarkdown, {
					markdown,
					color,
					variant,
					highlightText,
					children
				})]
			}), /* @__PURE__ */ jsx(CardButtons, { buttons })]
		})
	});
}
//#endregion
export { card_exports as n, Card as t };
